
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Airport } from "@/types/airport";
import { Button } from "@/components/ui/button";
import { Loader2, Plus } from "lucide-react";
import { AirportsTable } from "@/components/admin/airports/AirportsTable";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { useIsMobile } from "@/hooks/use-mobile";
import { AirportDialog } from "@/components/admin/AirportDialog";
import { useAirportManagement } from "@/hooks/useAirportManagement";
import { useAdminAccess } from "@/hooks/useAdminAccess";
import { DeleteAirportDialog } from "@/components/admin/airports/DeleteAirportDialog";

export default function AdminAirports() {
  const { isLoading: isCheckingAdmin } = useAdminAccess();
  const [isLoading, setIsLoading] = useState(true);
  const [airports, setAirports] = useState<Airport[]>([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [deletingAirport, setDeletingAirport] = useState<Airport | null>(null);
  const [editingAirport, setEditingAirport] = useState<Airport | null>(null);
  const isMobile = useIsMobile();
  
  const {
    sortField,
    sortDirection,
    sortedAirports,
    handleSort,
    handleDeleteAirport,
    handleUpdateAirport
  } = useAirportManagement(airports);

  useEffect(() => {
    if (!isCheckingAdmin) {
      fetchAirports();
    }
  }, [isCheckingAdmin]);

  const fetchAirports = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('airports')
        .select('*')
        .order('name');

      if (error) throw error;
      setAirports(data);
    } catch (error: any) {
      toast.error("Failed to fetch airports");
      console.error("Error fetching airports:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDialogSuccess = () => {
    fetchAirports();
    setIsDialogOpen(false);
    setEditingAirport(null);
  };

  const handleOpenDialog = () => {
    setEditingAirport(null);
    setIsDialogOpen(true);
  };

  const handleEditAirport = (airport: Airport) => {
    setEditingAirport(airport);
    setIsDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deletingAirport) return;
    
    const success = await handleDeleteAirport(deletingAirport.id);
    if (success) {
      fetchAirports();
    }
    setDeletingAirport(null);
  };

  if (isCheckingAdmin || isLoading) {
    return (
      <div className="container mx-auto py-8 px-4 flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <AdminLayout title="Manage Airports">
      <div className="space-y-8">
        <div className="flex justify-end">
          <Button 
            onClick={handleOpenDialog}
            size={isMobile ? "sm" : "default"}
          >
            <Plus className={isMobile ? "h-3 w-3 mr-1" : "h-4 w-4 mr-2"} />
            Add Airport
          </Button>
        </div>

        <div className="overflow-auto">
          <AirportsTable
            airports={sortedAirports}
            sortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
            onDelete={setDeletingAirport}
            onEdit={handleEditAirport}
          />
        </div>
      </div>

      <AirportDialog
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        editingAirport={editingAirport}
        airports={airports}
        onSuccess={handleDialogSuccess}
        onCancel={() => {
          setIsDialogOpen(false);
          setEditingAirport(null);
        }}
        updateAirport={handleUpdateAirport}
      />

      <DeleteAirportDialog
        airport={deletingAirport}
        onOpenChange={(open) => !open && setDeletingAirport(null)}
        onConfirm={handleConfirmDelete}
      />
    </AdminLayout>
  );
}
